/**
 * Keeps identifiers out of the parts of an event that group and get indexed.
 *
 * Two things go wrong without this. A URL carried into a title takes its query
 * string along, and a production issue has read
 * `[422] GET .../customer/sms/send?phone_number=…&recaptcha_token=…` — a
 * customer's phone number as the name of an issue. And a path with an id in it
 * is a different string on every request, so what is one failure becomes an
 * unbounded stream of issues nobody can total up.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ALL_DIGITS = /^\d+$/;

/**
 * Reduces a request URL to something safe to group by: origin and path, with
 * identifiers replaced by a placeholder.
 *
 * Masking runs per path segment rather than over the whole string, because the
 * whole-string form has to guess at what an id looks like and gets it wrong in
 * both directions — a three-digit order id slips through a "four or more
 * digits" rule, while `/api/v10/` gets mangled by it. A segment that is
 * entirely digits is an id and a segment that is a UUID is an id; `v2` is
 * neither, so versions survive.
 */
export const sanitizeUrl = (raw: string): string => {
  const withoutQuery = raw.split('?')[0]?.split('#')[0] ?? '';
  const [scheme, rest] = /^https?:\/\//.test(withoutQuery)
    ? [
        withoutQuery.slice(0, withoutQuery.indexOf('//') + 2),
        withoutQuery.slice(withoutQuery.indexOf('//') + 2),
      ]
    : ['', withoutQuery];

  const segments = rest.split('/').map((segment, index) =>
    // Index 0 after a scheme is the host, which is not an id however it reads.
    (scheme && index === 0) ||
    (!ALL_DIGITS.test(segment) && !UUID.test(segment))
      ? segment
      : ':id',
  );

  return scheme + segments.join('/');
};

/**
 * Field names whose values never belong in a report.
 *
 * Matched on the key rather than the value, because the values are exactly the
 * ones that cannot be recognised by shape: a one-time code looks like a
 * number, a client secret looks like a string. Matching is substring and
 * case-insensitive so `phone_number`, `mobileMoneyPhone` and `PhoneNumber` all
 * land the same way.
 */
const SENSITIVE_KEYS = [
  'authorization',
  'card',
  'client_secret',
  'clientsecret',
  'cvv',
  'otp',
  'password',
  'phone',
  'recaptcha',
  'secret',
  'token',
];

/**
 * Splits a key into words so matching is by token, not by substring.
 *
 * `warehouse_code` must survive a rule about `code`, and a raw substring test
 * cannot tell the two apart.
 */
const keyTokens = (key: string): string[] =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[^a-zA-Z0-9]+/)
    .map((token) => token.toLowerCase())
    .filter(Boolean);

const isSensitive = (key: string): boolean => {
  const tokens = keyTokens(key);
  return SENSITIVE_KEYS.some(
    (needle) => tokens.includes(needle) || key.toLowerCase() === needle,
  );
};

/**
 * Scrubs a string that is not itself under a sensitive key.
 *
 * Two things hide inside strings, and neither can be caught by looking at the
 * key. A URL carries its query string, which is how a customer's phone number
 * reached an issue title before `sanitizeUrl` existed — and it reaches extras
 * the same way, because an AxiosError brings its whole `config` along. And a
 * request body has already been serialised by the time an interceptor sees it,
 * so `{"otp":"…"}` is a plain string that key matching looks straight past.
 */
const scrubString = (value: string): unknown => {
  if (
    /^https?:\/\//.test(value) ||
    (value.startsWith('/') && value.includes('?'))
  ) {
    return sanitizeUrl(value);
  }
  const trimmed = value.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        return redactSensitive(parsed);
      }
    } catch {
      // Not JSON after all; nothing to look inside.
    }
  }
  return value;
};

/**
 * Replaces sensitive values with a marker, keeping the shape.
 *
 * Booleans are never redacted: `true` and `false` carry no secret, and the
 * fields that read as sensitive by name are usually exactly the useful ones —
 * `saveCard`, `hasToken`, `hasEncryptedCard` all say whether something was
 * present, which is frequently the thing being debugged. Empty values are kept
 * for the same reason.
 */
export const redactSensitive = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return scrubString(value);
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(redactSensitive);
  }
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    if (
      typeof item === 'boolean' ||
      item === undefined ||
      item === null ||
      item === ''
    ) {
      out[key] = item;
    } else if (isSensitive(key)) {
      out[key] = '[redacted]';
    } else {
      out[key] = redactSensitive(item);
    }
  }
  return out;
};

/** Serialised size above which an extras payload is replaced by a note. */
const MAX_EXTRAS_BYTES = 16_384;
const MAX_DEPTH = 4;

const prune = (value: unknown, depth: number): unknown => {
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (depth >= MAX_DEPTH) {
    return Array.isArray(value) ? `[Array(${value.length})]` : '[Object]';
  }
  if (Array.isArray(value)) {
    return value.slice(0, 50).map((item) => prune(item, depth + 1));
  }

  // `Object.entries` only sees own *enumerable* properties, and the ones that
  // carry the meaning on these types are not enumerable: an Error walked that
  // way serialises to `{}`, losing its message and stack, and a Date or a Map
  // does the same. Each is unwrapped explicitly instead. For an AxiosError the
  // enumerable extras — `code`, `response` — are then merged back on top.
  if (value instanceof Error) {
    const out: Record<string, unknown> = {
      name: value.name,
      message: value.message,
      stack: value.stack?.split('\n').slice(0, 12).join('\n'),
    };
    for (const [key, item] of Object.entries(value)) {
      out[key] = prune(item, depth + 1);
    }
    return out;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (value instanceof Map) {
    return Object.fromEntries(
      [...value.entries()]
        .slice(0, 50)
        .map(([key, item]) => [String(key), prune(item, depth + 1)]),
    );
  }
  if (value instanceof Set) {
    return [...value].slice(0, 50).map((item) => prune(item, depth + 1));
  }

  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    out[key] = prune(item, depth + 1);
  }
  return out;
};

/**
 * Bounds an extras payload in depth and size.
 *
 * The SDK is configured with `normalizeDepth: 3`, which trims what it sends
 * but only after the whole object has been walked and serialised; a cart with
 * every product expanded is paid for either way. Trimming at the call site is
 * what actually keeps the payload small.
 */
export const capExtras = (
  extras: Record<string, unknown>,
): Record<string, unknown> => {
  // Redaction runs here rather than at the call sites so that forgetting it is
  // not possible: every payload that reaches Sentry, from a report or from a
  // breadcrumb, goes through this function.
  let pruned: Record<string, unknown>;
  let size: number;
  // The whole body is guarded, not just the serialisation. `prune` walks
  // values the app never chose deliberately — an invalid Date throws on
  // `toISOString`, an enumerable getter can throw anything — and this runs
  // inside `emit` after the budget has been spent and `once` marked, so an
  // escape here would lose the report *and* the allowance for it, then throw
  // into whatever business code was reporting.
  try {
    pruned = redactSensitive(prune(extras, 0)) as Record<string, unknown>;
    size = JSON.stringify(pruned)?.length ?? 0;
  } catch {
    return { note: '[telemetry] extras could not be prepared' };
  }
  if (size <= MAX_EXTRAS_BYTES) {
    return pruned;
  }
  return {
    note: `[telemetry] extras omitted — ${size} bytes exceeds the ${MAX_EXTRAS_BYTES} byte cap`,
    keys: Object.keys(pruned),
  };
};
